export type WellTypeRatio = {
  injection: number;
  production: number;
};

export const DEFAULT_WELL_TYPE_RATIO: WellTypeRatio = {
  injection: 1,
  production: 1,
};

const MAX_RATIO_PART = 999;

/** Keep only digits for ratio text fields. */
export function sanitizeRatioPartInput(raw: string): string {
  return raw.replace(/\D/g, '').slice(0, 3);
}

export function normalizeWellTypeRatio(ratio: Partial<WellTypeRatio> | undefined): WellTypeRatio {
  const injection = Math.max(1, Math.min(MAX_RATIO_PART, Math.floor(ratio?.injection ?? 1)));
  const production = Math.max(1, Math.min(MAX_RATIO_PART, Math.floor(ratio?.production ?? 1)));
  return { injection, production };
}

export function parseRatioPartInput(raw: string, fallback: number): number {
  const t = raw.trim();
  if (t === '') {
    return fallback;
  }
  const n = Number.parseInt(t, 10);
  if (!Number.isFinite(n) || n < 1) {
    return fallback;
  }
  return Math.min(MAX_RATIO_PART, n);
}

function targetCumulativeInjectionWells(
  cumulativeProduction: number,
  ratio: WellTypeRatio,
): number {
  if (cumulativeProduction <= 0) {
    return 0;
  }
  const { injection: injUnits, production: prodUnits } = normalizeWellTypeRatio(ratio);
  return Math.round((cumulativeProduction * injUnits) / prodUnits);
}

/**
 * Per-year injection counts that keep the cumulative injection/production ratio
 * aligned with the configured integer ratio across the full production schedule.
 */
export function computeInjectionWellsFromRatio(
  productionPerYear: number[],
  ratio: WellTypeRatio,
): number[] {
  let cumulativeProduction = 0;
  let previousCumulativeInjection = 0;

  return productionPerYear.map((countRaw) => {
    const production = Math.max(0, Math.floor(countRaw || 0));
    cumulativeProduction += production;

    const targetCumulativeInjection = targetCumulativeInjectionWells(cumulativeProduction, ratio);
    const yearInjection = Math.max(0, targetCumulativeInjection - previousCumulativeInjection);
    previousCumulativeInjection = targetCumulativeInjection;

    return yearInjection;
  });
}

export function formatWellTypeRatioHint(ratio: WellTypeRatio): string {
  const { injection, production } = normalizeWellTypeRatio(ratio);
  const injLabel = injection === 1 ? 'injection well' : 'injection wells';
  const prodLabel = production === 1 ? 'production well' : 'production wells';
  return `${injection} cumulative ${injLabel} per ${production} cumulative ${prodLabel}`;
}
